import { useEffect, useState, useMemo } from "react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { brandService } from "../../services/brand.service";
import { usePortalScope } from "@/contexts/portalScope";
import {
  Facebook,
  Instagram,
  Youtube,
  ArrowLeft,
  ArrowRight,
} from "react-feather";
import { DiscordIcon } from "../../assets/discord";
import TikTokIcon from "../../assets/tiktok";
import { ShowcaseCarousel } from "../../components/ShowcaseCarousel";
import { ProductShowcase } from "../../components/ProductShowcase";
import type {
  BrandDetails as BrandDetailsType,
  AppearanceSettings,
} from "../../types/brand";
import { ICON_SIZES } from "../../types/brand";
import { JoinBrandModal } from "../../components/affiliate/JoinBrandModal";
import {
  programService,
  JoinProgramResponse,
} from "../../services/program.service";
import { toast } from "react-hot-toast";
import { formatSocialMediaUrl } from "../../utils/socialMediaUtils";
import {
  paymentMethodsService,
  BrandSupportedPaymentMethod,
  AffiliatePaymentMethod,
} from "../../services/paymentMethods.service";
import { useAuth } from "../../hooks/useAuth";
import {
  getThemeLayout,
  getThemeStyles,
  processYouTubeContent,
  getAppearanceClasses,
  getColors,
} from "../../utils/themeUtils";
import { DEFAULT_BRAND } from "../../theme/brandTheme";
import { useBrandTheme } from "../../theme/useBrandTheme";
import { useMediaQuery } from "react-responsive";
import { driver } from "driver.js";
import { useAffiliate } from "@/contexts/affiliate";
import { formatstorageUrl } from "@/utils/formatters";
import {
  readProgramToken,
  stripProgramTokenFromSearch,
} from "../../utils/programShareLink";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";

import { acceptAdRequest, AD_REQUEST_TOKEN_KEY, adRequestSubmitPath } from "@/services/affiliateAdRequest.service";
export function BrandDetailView() {
  const { details: affiliateData } = useAffiliate();
  const { isMarketplaceHidden } = usePortalScope();
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // `?p=<token>` selects one of the brand's programs. Absent → default program.
  const programToken = readProgramToken(location.search);
  const [brandData, setBrandData] = useState<BrandDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [defaultCode, setDefaultCode] = useState("");
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [shouldAutoJoin, setShouldAutoJoin] = useState(false);
  // Token from a TikTok ad-request link. It pre-approves the join and, once
  // the affiliate is in, is accepted so they land on the submit page.
  const [adRequestToken, setAdRequestToken] = useState<string | null>(null);
  const [hasJoined, setHasJoined] = useState(false);
  const [hasPendingApplication, setHasPendingApplication] = useState(false);
  // Auto-join must never fire before we know whether the affiliate is already
  // enrolled — otherwise an enrolled affiliate gets the join modal popped open
  // and the submit bounces off the backend's duplicate check.
  const [joinStatusChecked, setJoinStatusChecked] = useState(false);
  const [appearanceSettings, setAppearanceSettings] =
    useState<AppearanceSettings | null>(null);
  const isMobile = useMediaQuery({ query: "(max-width: 1366px)" });

  // Payment method states
  const [supportedPaymentMethods, setSupportedPaymentMethods] = useState<
    BrandSupportedPaymentMethod[]
  >([]);
  const [affiliatePaymentMethods, setAffiliatePaymentMethods] = useState<
    AffiliatePaymentMethod[]
  >([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);

  // Get CSS classes based on appearance settings
  const appearanceClasses = useMemo(() => {
    return getAppearanceClasses(appearanceSettings);
  }, [appearanceSettings]);

  // Get color values from appearance settings
  const colors = useMemo(() => {
    return getColors(appearanceSettings);
  }, [appearanceSettings]);

  // Apply this brand's colors to the brand-* tokens while its page is open;
  // reset on leave so they don't bleed into the rest of the affiliate portal.
  useBrandTheme(appearanceSettings, true);

  // Get theme-specific layout and styles
  const themeLayout = useMemo(() => {
    const theme = appearanceSettings?.theme || "TYPE_1";
    return getThemeLayout(theme);
  }, [appearanceSettings?.theme]);

  const themeStyles = useMemo(() => {
    const theme = appearanceSettings?.theme || "TYPE_1";
    return getThemeStyles(theme, colors);
  }, [appearanceSettings?.theme, colors]);

  useEffect(() => {
    // Check URL parameters for autoJoin flag and source
    const searchParams = new URLSearchParams(location.search);
    const autoJoinParam = searchParams.get("autoJoin");

    // Check session storage for shouldAutoJoin flag (set during login/signup flow)
    const sessionAutoJoin = sessionStorage.getItem("shouldAutoJoin");

    if (autoJoinParam === "true" || sessionAutoJoin === "true") {
      setShouldAutoJoin(true);

      // Clear the session storage flag after reading it
      if (sessionAutoJoin) {
        sessionStorage.removeItem("shouldAutoJoin");
      }

      // Strip the param too — otherwise every reload / back-forward re-arms
      // auto-join and re-opens the modal for an already-enrolled affiliate.
      if (autoJoinParam === "true") {
        searchParams.delete("autoJoin");
        const search = searchParams.toString();
        navigate(
          { pathname: location.pathname, search: search ? `?${search}` : "" },
          { replace: true }
        );
      }
    }

    const adToken = searchParams.get("adRequest") ?? sessionStorage.getItem(AD_REQUEST_TOKEN_KEY);
    if (adToken) {
      setAdRequestToken(adToken);
      setShouldAutoJoin(true);
      sessionStorage.setItem(AD_REQUEST_TOKEN_KEY, adToken);
    }

    // Check if we have a stored username that matches the current username
    const storedUsername = sessionStorage.getItem("pendingJoinBrandUsername");
    if (storedUsername && storedUsername === username) {
      setShouldAutoJoin(true);
      // Clean up session storage
      sessionStorage.removeItem("pendingJoinBrandUsername");
      sessionStorage.removeItem("pendingJoinBrandId");
      sessionStorage.removeItem("pendingJoinBrandName");
      sessionStorage.removeItem("pendingJoinBrandPaymentMethods");
      sessionStorage.removeItem("brandJoinReturnUrl");
    }
  }, [location.search, location.pathname, navigate, username]);

  useEffect(() => {
    const fetchBrandData = async () => {
      if (!username || !user?.id) return;

      try {
        setIsLoading(true);
        setJoinStatusChecked(false);
        const data = await brandService.getBrandDetailsByUsername(
          username,
          programToken
        );
        setBrandData(data);

        // Stale/unknown share token: say so, then carry on with the default
        // program. Dropping the param stops the toast repeating on reload and
        // keeps the join request from re-sending a token the API rejected.
        //
        // The fixed toast id collapses duplicates: this effect re-runs on
        // `programToken` and on `user?.id` settling, and StrictMode double-
        // invokes it in dev, so the same warning could otherwise stack up.
        if (data.programTokenInvalid) {
          toast.error("Program not found — showing the default instead.", {
            id: "program-token-invalid",
          });
          navigate(
            {
              pathname: location.pathname,
              search: stripProgramTokenFromSearch(location.search),
            },
            { replace: true }
          );
        }

        // Use appearance settings from brand data
        if (data.brandAppearance) {
          setAppearanceSettings(data.brandAppearance);
        } else {
          // Set default appearance settings if not available
          setAppearanceSettings({
            joinText: data.program?.joinText || "Join the team",
            joinTitle: data.program?.joinTitle || "",
            ambassadorText:
              data.program?.ambassadorText || "Become a brand ambassador",
            theme: "TYPE_1",
            socialMediaIconSizes: "medium",
            primayColour: DEFAULT_BRAND.primary,
            secondaryColour: DEFAULT_BRAND.secondary,
            imageIds: [],
            showcaseProductIds: [],
          });
        }

        // Check if the affiliate has already joined this brand
        if (data.program?.id) {
          try {
            // `joined` now means "in THIS program". Being in a different
            // program of the same brand comes back as `switchFrom`, and the
            // join modal handles the comparison — so the Join button must stay
            // live rather than reading as already-joined.
            const joinStatus = await programService.checkIfJoined(
              data.program.id
            );
            setHasJoined(joinStatus.joined);
            setHasPendingApplication(joinStatus.pendingApplication);
          } catch {
            // console.error("Error checking if joined:", error);
            // Unknown join state — never auto-submit a join on top of it.
            setShouldAutoJoin(false);
            toast.error("Failed to load brand details");
          } finally {
            setJoinStatusChecked(true);
          }
        } else {
          setJoinStatusChecked(true);
        }

        // Fetch brand supported payment methods
        if (data.id) {
          await fetchBrandSupportedPaymentMethods(data.id);
        }
        
      } catch {
        toast.error("Failed to load brand details");
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch data if we have both username and user
    if (username && user?.id) {
      fetchBrandData();
    }
    // programToken is in here on purpose: arriving from the public page swaps
    // the query string without remounting, and clearing a stale token has to
    // refetch the default program.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, user?.id, programToken]);

  useEffect(() => {
    if (
      shouldAutoJoin &&
      brandData &&
      joinStatusChecked &&
      !hasJoined &&
      !hasPendingApplication
    ) {
      setTimeout(() => {
        handleJoinProcess(true);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutoJoin,
    brandData,
    joinStatusChecked,
    hasJoined,
    hasPendingApplication,
  ]);

  // Fetch brand supported payment methods
  const fetchBrandSupportedPaymentMethods = async (brandId: string) => {
    if (!user?.id) return;

    try {
      setIsLoadingPaymentMethods(true);

      // Fetch brand supported payment methods
      const supportedMethods =
        await paymentMethodsService.getBrandSupportedPaymentMethods(brandId);
      setSupportedPaymentMethods(supportedMethods);

      // Fetch affiliate payment methods
      const affiliateMethods =
        await paymentMethodsService.getAffiliatePaymentInfo(user.id);
      setAffiliatePaymentMethods(affiliateMethods);
    } catch {
      toast.error("Failed to load brand details");
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  // Button click handler for join button
  const handleJoinClick = (event: React.MouseEvent) => {
    event.preventDefault();
    handleJoinProcess(false);
  };

  useEffect(() => {
    if (
      (affiliateData?.socialLinks?.length ?? 0) < 3 &&
      hasPendingApplication &&
      brandData?.program.automaticApproval === false
    ) {
      const driverObj = driver();
      driverObj.highlight({
        popover: {
          popoverClass:
            "!max-w-[420px] !min-w-[320px]",
          description: `
            <div class="space-y-4">
              <div>
                <h2 class="text-base font-semibold text-foreground">Complete your social profile</h2>
                <p class="text-sm text-muted-foreground mt-1">
                  Brands reviewing your application look at your social presence.
                  Adding your links makes it easier for them to say yes.
                </p>
              </div>

              <div class="flex items-center gap-2 text-sm text-muted-foreground">
                <span class="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs">Instagram</span>
                <span class="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs">TikTok</span>
                <span class="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs">YouTube</span>
              </div>

              <button
                onclick="window.location.href='/affiliate/settings?tab=profile'"
                class="w-full bg-primary text-primary-foreground hover:opacity-90 px-4 py-2 rounded-md text-sm font-medium transition-opacity"
              >
                Add social links
              </button>
            </div>
          `,
        },
      });
    }
  }, [hasPendingApplication]);

  // Internal function to handle join process with auto-join parameter
  const handleJoinProcess = async (isAutoJoin = false) => {
    if (!brandData?.program?.id) return;

    try {
      setIsGeneratingCode(true);

      const code = await programService.generateCode(brandData.program.id);
      setDefaultCode(code);

      // Always show the join modal, whether auto-join or manual join
      setShowJoinModal(true);

      // Reset auto-join flag after showing the modal
      if (isAutoJoin) {
        setShouldAutoJoin(false);
      }
    } catch {
      toast.error("Could not generate affiliate code");

      // Reset auto-join flag if this was an auto-join attempt
      if (isAutoJoin) {
        setShouldAutoJoin(false);
      }
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Once enrolled, bind the ad request to this affiliate and go straight to
  // the submit page it points at. Any failure leaves them on the brand page.
  const finishAdRequest = async () => {
    if (!adRequestToken) return;
    try {
      const accepted = await acceptAdRequest(adRequestToken);
      sessionStorage.removeItem(AD_REQUEST_TOKEN_KEY);
      setAdRequestToken(null);
      navigate(adRequestSubmitPath(accepted.programId, accepted.id));
    } catch (error) {
      const body = (error as { response?: { data?: { message?: string } } })?.response?.data;
      toast.error(body?.message || "Could not open the ad request");
    }
  };

  // Already enrolled when the ad-request link arrives: nothing to join, so
  // accept straight away.
  useEffect(() => {
    if (adRequestToken && joinStatusChecked && hasJoined) void finishAdRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adRequestToken, joinStatusChecked, hasJoined]);

  const handleJoinSuccess = (response: JoinProgramResponse) => {
    // Drive the message off the record we got back, not the join/apply flags —
    // an enrollment comes back APPROVED, an application comes back PENDING.
    const status = response?.data?.status;
    setHasJoined(status === "APPROVED");
    setHasPendingApplication(status === "PENDING");
    setShowJoinModal(false);
    toast.success(
      status === "APPROVED"
        ? "Successfully joined the program!"
        : "Application submitted successfully!"
    );
    if (status === "APPROVED") void finishAdRequest();
  };

  // The backend rejects a second join with ALREADY_ENROLLED. That means the
  // affiliate is already in — reconcile the UI instead of showing an error.
  const handleAlreadyJoined = (status?: string) => {
    setHasJoined(status !== "PENDING");
    setHasPendingApplication(status === "PENDING");
    setShouldAutoJoin(false);
    setShowJoinModal(false);
    if (status !== "PENDING") void finishAdRequest();
  };

  if (isLoading || !brandData) {
    return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
    );
  }

  // Which program the affiliate is about to join. Only worth showing when the
  // link named one — the default program's card already reads as "the" program.
  const programBadge =
    brandData.isSharedProgram && brandData.program?.name ? (
      <div
        className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full text-xs font-medium"
        style={{
          backgroundColor: `${colors.primary}15`,
          color: colors.primary,
        }}
      >
        <span className="opacity-70">Program</span>
        <span>{brandData.program.name}</span>
      </div>
    ) : null;

  return (
    <div
      className={`min-h-screen transition-all duration-500`}
      style={{
        backgroundColor: `${colors.secondary}05`,
      }}
    >
      {/* Banner Section */}
      <div className="relative">
        {/* Back Button */}
        <div className="absolute top-8 left-4 z-20">
          <button
            onClick={() =>
              navigate(
                isMarketplaceHidden
                  ? "/affiliate/brands"
                  : "/affiliate/brands/search"
              )
            }
            className={`inline-flex items-center px-3 py-2 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105`}
            style={{ backgroundColor: colors.primary }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        </div>

        {/* Banner */}
        <div className={`relative ${themeLayout.bannerHeight}`}>
          {/* Banner Image: mobile banner on small screens when set, else desktop */}
          {(brandData.brandBannerImage || brandData.brandBannerImageMobile) && (
            <picture>
              {brandData.brandBannerImageMobile && (
                <source
                  media="(max-width: 768px)"
                  srcSet={formatstorageUrl(brandData.brandBannerImageMobile)}
                />
              )}
              <img
                src={formatstorageUrl(
                  brandData.brandBannerImage || brandData.brandBannerImageMobile || ""
                )}
                alt="Brand Banner"
                className={themeLayout.bannerImage}
              />
            </picture>
          )}
          {/* Banner Overlay */}
          <div className={themeLayout.bannerOverlay} />
          {/* Banner Background Fallback */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${colors.secondary}, white)`,
            }}
          />
        </div>
      </div>

      <main className={themeLayout.container}>
        {/* Profile Section */}
        <div
          className={`${themeLayout.profileSection} ${themeLayout.profilePosition}`}
        >
          {/* Profile Image */}
          <div
            className={`${themeLayout.profileImage} ${
              appearanceSettings?.theme === "TYPE_4"
                ? "hover:scale-110 hover:-rotate-6 transition-all duration-300 ease-out"
                : ""
            }`}
          >
            <div className={themeLayout.profileImageSize}>
              <div className="w-full h-full p-2 rounded-full bg-card shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <BrandLogo
                  src={brandData.brandProfileImage}
                  name={
                    brandData.companyName ||
                    `${brandData.firstName ?? ""} ${brandData.lastName ?? ""}`
                  }
                  primaryColor={colors.primary}
                  className="w-full h-full rounded-full text-2xl transition-all duration-300 hover:scale-105"
                />
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div
            className={`${themeLayout.profileInfo} ${appearanceClasses.spacing}`}
          >
            {/* Company Name and Niches */}
            <div className={`flex flex-col sm:flex-row sm:items-center gap-4`}>
              <h1
                className={`text-[28px] font-semibold text-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} text-center lg:text-left`}
              >
                {brandData.companyName ||
                  brandData.firstName + " " + brandData.lastName}
              </h1>
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                {brandData.niches?.map((niche) => (
                  <span
                    key={niche.id}
                    className={`${themeStyles.nicheTag} transition-all duration-300 cursor-pointer`}
                    style={{
                      backgroundColor: `${colors.primary}20`,
                      color: colors.primary,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.primary}30`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.primary}20`;
                    }}
                  >
                    {niche.name}
                  </span>
                ))}
              </div>
            </div>

            {/* Website */}
            <div
              className={`mt-2 flex items-center gap-2 justify-center sm:justify-start`}
            >
              <span
                className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
              >
                Website:
              </span>
              {brandData.website && (
                <a
                  href={brandData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-foreground break-all transition-colors duration-300 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  style={{ color: "#111827" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#111827";
                  }}
                >
                  {brandData.website.includes(".myshopify.com")
                    ? "Click here"
                    : brandData.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>

            {/* Socials */}
            <div
              className={`mt-2 flex flex-wrap items-center gap-4 justify-center sm:justify-start`}
            >
              <span
                className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
              >
                Socials
              </span>
              <div className="flex items-center gap-6">
                {Array.isArray(brandData.socialMedia) &&
                brandData.socialMedia.length > 0 ? (
                  brandData.socialMedia.map((social) => {
                    const platform = social.socialMediaPlatform.name;
                    // Use the utility function to format the social media URL properly
                    const url =
                      formatSocialMediaUrl(social.username, platform) ||
                      `${social.socialMediaPlatform.url}/${social.username}`;

                    return (
                      <a
                        key={social.id}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${themeStyles.socialIcon}`}
                        title={`${
                          platform.charAt(0) + platform.slice(1).toLowerCase()
                        }: ${social.username}`}
                      >
                        {platform === "FACEBOOK" && (
                          <Facebook
                            className={
                              ICON_SIZES.find(
                                (size) =>
                                  size.label.toUpperCase() ===
                                  appearanceSettings?.socialMediaIconSizes
                              )?.size || "w-5 h-5"
                            }
                          />
                        )}
                        {platform === "INSTAGRAM" && (
                          <Instagram
                            className={
                              ICON_SIZES.find(
                                (size) =>
                                  size.label.toUpperCase() ===
                                  appearanceSettings?.socialMediaIconSizes
                              )?.size || "w-5 h-5"
                            }
                          />
                        )}
                        {platform === "TIKTOK" && (
                          <TikTokIcon
                            className={
                              ICON_SIZES.find(
                                (size) =>
                                  size.label.toUpperCase() ===
                                  appearanceSettings?.socialMediaIconSizes
                              )?.size || "w-5 h-5"
                            }
                          />
                        )}
                        {platform === "YOUTUBE" && (
                          <Youtube
                            className={
                              ICON_SIZES.find(
                                (size) =>
                                  size.label.toUpperCase() ===
                                  appearanceSettings?.socialMediaIconSizes
                              )?.size || "w-5 h-5"
                            }
                          />
                        )}
                        {platform === "DISCORD" && (
                          <DiscordIcon
                            className={
                              ICON_SIZES.find(
                                (size) =>
                                  size.label.toUpperCase() ===
                                  appearanceSettings?.socialMediaIconSizes
                              )?.size || "w-5 h-5"
                            }
                          />
                        )}
                      </a>
                    );
                  })
                ) : (
                  <span className="text-muted-foreground text-sm">
                    No social media links available
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={themeLayout.contentGrid}>
          {/* Main Content */}
          <div
            className={`${themeLayout.description} ${themeLayout.statsPosition}`}
          >
            {/* Stats */}
            <div className={themeStyles.statsContainer}>
              <div className={themeLayout.statsGrid}>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                >
                  <div
                    className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    style={{ color: colors.primary }}
                  >
                    #{brandData.stats?.rank || "0"}
                  </div>
                  <div className="text-sm text-muted-foreground">Rank</div>
                </div>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                >
                  <div
                    className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    style={{ color: colors.primary }}
                  >
                    {brandData.stats?.products || "0"}
                  </div>
                  <div className="text-sm text-muted-foreground">Products</div>
                </div>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                >
                  <div
                    className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    style={{ color: colors.primary }}
                  >
                    {Number(brandData.stats?.team || 0) >= 1000
                      ? `${parseFloat((Number(brandData.stats?.team || 0) / 1000).toFixed(1))}K+`
                      : brandData.stats?.team || "0"}
                  </div>
                  <div className="text-sm text-muted-foreground">Team</div>
                </div>
                <div
                  className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                >
                  <div
                    className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    style={{ color: colors.primary }}
                  >
                    {Number(brandData.stats?.orders || 0) >= 1000
                      ? `${parseFloat((Number(brandData.stats?.orders || 0) / 1000).toFixed(1))}K+`
                      : brandData.stats?.orders || "0"}
                  </div>
                  <div className="text-sm text-muted-foreground">Orders</div>
                </div>
              </div>
            </div>

            {/* Description */}
            {brandData.description && (
              <div
                className={`${themeStyles.descriptionContainer} ${themeLayout.descriptionPosition}`}
              >
                <div
                  className={`text-muted-foreground prose max-w-none ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  dangerouslySetInnerHTML={{
                    __html: processYouTubeContent(brandData.description),
                  }}
                />
              </div>
            )}

            {/* Join Card */}
            {isMobile && brandData?.program && (
              <div className={themeLayout.joinCard}>
                <div
                  className={`${themeStyles.joinCard} mt-8 transition-all duration-300 hover:shadow-xl`}
                >
                  {hasJoined ? (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-6 h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <h2
                        className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      >
                        You're an Ambassador!
                      </h2>
                      <div
                        className={`prose max-w-none ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                        dangerouslySetInnerHTML={{
                          __html: processYouTubeContent(
                            appearanceSettings?.ambassadorText ||
                              brandData.program?.ambassadorText ||
                              ""
                          ),
                        }}
                      />
                    </div>
                  ) : hasPendingApplication ? (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-6 h-6 text-yellow-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </div>
                      <h2
                        className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      >
                        Application Pending
                      </h2>
                      <p
                        className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      >
                        You have already applied to join the{" "}
                        {brandData.companyName} affiliate program. We'll notify
                        you once your application is reviewed.
                      </p>
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border-l-4 border-amber-400">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-start flex-shrink-0 mt-0.5">
                            <span className="text-white text-xs font-bold">
                              💡
                            </span>
                          </div>
                          <div>
                            <p className="text-amber-800 font-medium text-sm mb-1">
                              Pro Tip
                            </p>
                            <p className="text-amber-700 text-sm">
                              Complete profiles with social media links are more
                              likely to be accepted by brands!
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  navigate("/affiliate/settings?tab=profile");
                                }}
                              >
                                Go to settings
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {programBadge}
                      <h2
                        className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      >
                        {appearanceSettings?.joinTitle ||
                          brandData.program?.joinTitle ||
                          appearanceSettings?.joinText ||
                          "Join the team"}
                      </h2>
                      <p
                        className={`text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      >
                        {(() => {
                          const processedContent = processYouTubeContent(
                            appearanceSettings?.joinText ||
                              brandData.program?.joinText ||
                              ""
                          );
                          return (
                            <div
                              className="prose max-w-none"
                              dangerouslySetInnerHTML={{
                                __html: processedContent,
                              }}
                            />
                          );
                        })()}
                      </p>
                      <div
                        className={`text-sm text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} ${appearanceClasses.animation}`}
                      >
                        {brandData.program?.commissionValue}% commission on all
                        code uses
                      </div>

                      {/* Payment Methods Display */}
                      {isLoadingPaymentMethods ? (
                        <div className="mb-4">
                          <p className="text-sm text-muted-foreground mb-2">
                            Loading payment methods...
                          </p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {[1, 2, 3].map((i) => (
                              <span
                                key={i}
                                className="px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-border animate-pulse"
                              ></span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        supportedPaymentMethods.length > 0 && (
                          <div className="mb-4 text-center">
                            <p className="text-sm text-muted-foreground mb-2">
                              Available payment methods
                            </p>
                            <div className="flex flex-wrap gap-2 justify-center">
                              {supportedPaymentMethods.map((method) => (
                                <span
                                  key={method.id}
                                  className={`px-3 py-1 text-xs rounded-full ${
                                    affiliatePaymentMethods.some(
                                      (am) =>
                                        am.paymentMethodId ===
                                        method.paymentMethodId
                                    )
                                      ? "bg-brand-100 text-brand-700 border border-brand-300"
                                      : "bg-muted text-muted-foreground border border-border"
                                  }`}
                                >
                                  {method.paymentMethod.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                      {!shouldAutoJoin && (
                        <button
                          onClick={handleJoinClick}
                          disabled={isGeneratingCode}
                          className={`w-full flex items-center justify-center text-white py-2 ${appearanceClasses.borderRadius} ${appearanceClasses.shadow} hover:shadow-md transition-all duration-200 transform hover:scale-105`}
                          style={{
                            backgroundColor: colors.primary,
                            filter: "brightness(1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.filter = "brightness(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.filter = "brightness(1)";
                          }}
                        >
                          {isGeneratingCode ? (
                            <>
                              <Spinner
                                size="sm"
                                className="-ml-1 mr-3 border-white"
                              />
                              Generating code...
                            </>
                          ) : (
                            "Join Now"
                          )}
                        </button>
                      )}
                      {shouldAutoJoin && isGeneratingCode && (
                        <div className="w-full bg-brand-100 text-brand-700 py-2 px-4 rounded-lg text-center">
                          <div className="flex items-center justify-center">
                            <Spinner
                              size="sm"
                              className="-ml-1 mr-3 border-brand-700"
                            />
                            Processing your join request...
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Showcase Images Carousel */}
            {appearanceSettings?.imageIds &&
              appearanceSettings.imageIds.length > 0 && (
                <div
                  className={`${themeStyles.showcaseContainer} ${themeLayout.showcasePosition}`}
                >
                  <h3
                    className={`text-xl font-semibold mb-4 text-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    Brand Showcase
                  </h3>
                  <ShowcaseCarousel
                    images={appearanceSettings.imageIds}
                    autoPlay={true}
                    interval={4000}
                    showControls={true}
                    className={`${appearanceClasses.shadow} ${appearanceClasses.borderRadius}`}
                  />
                </div>
              )}

            {/* Product Showcase */}
            {appearanceSettings?.showcaseProductIds &&
              appearanceSettings.showcaseProductIds.length > 0 && (
                <div
                  className={`${themeStyles.showcaseContainer} ${themeLayout.showcasePosition}`}
                >
                  <ProductShowcase
                    selectedProductIds={appearanceSettings.showcaseProductIds}
                    appearanceSettings={appearanceSettings}
                    className={`${appearanceClasses.shadow} ${appearanceClasses.borderRadius}`}
                  />
                </div>
              )}
          </div>

          {/* Join Card */}
          {!isMobile && brandData?.program && (
            <div className={themeLayout.joinCard}>
              <div
                className={`${themeStyles.joinCard} transition-all duration-300 hover:shadow-xl`}
              >
                {hasJoined ? (
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                    </div>
                    <h2
                      className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      You're an Ambassador!
                    </h2>
                    <div
                      className={`prose max-w-none ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      dangerouslySetInnerHTML={{
                        __html: processYouTubeContent(
                          appearanceSettings?.ambassadorText ||
                            brandData.program?.ambassadorText ||
                            ""
                        ),
                      }}
                    />
                  </div>
                ) : hasPendingApplication ? (
                  <div className="text-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-6 h-6 text-yellow-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </div>
                    <h2
                      className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      Application Pending
                    </h2>
                    <p
                      className={`text-muted-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      You have already applied to join the{" "}
                      {brandData.companyName} affiliate program. We'll notify
                      you once your application is reviewed.
                    </p>
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border-l-4 border-amber-400">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0 ">
                          <span className="text-white text-center text-xs font-bold">
                            💡
                          </span>
                        </div>
                        <div className="flex flex-col justify-center items-start gap-2">
                          <p className="text-amber-800 font-medium text-sm mb-1">
                            Pro Tip
                          </p>
                          <p className="text-amber-700 text-justify text-sm">
                            Complete profiles with social media links are more
                            likely to be accepted by brands!
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                navigate("/affiliate/settings?tab=profile");
                              }}
                            >
                              Go to settings
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {programBadge}
                    <h2
                      className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      {appearanceSettings?.joinTitle ||
                        brandData.program?.joinTitle ||
                        "Join the team"}
                    </h2>
                    <p
                      className={`text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      {(() => {
                        const processedContent = processYouTubeContent(
                          appearanceSettings?.joinText ||
                            brandData.program?.joinText ||
                            ""
                        );
                        return (
                          <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{
                              __html: processedContent,
                            }}
                          />
                        );
                      })()}
                    </p>
                    <div
                      className={`text-sm text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight} ${appearanceClasses.animation}`}
                    >
                      {brandData.program?.commissionValue}% commission on all
                      code uses
                    </div>

                    {/* Payment Methods Display */}
                    {isLoadingPaymentMethods ? (
                      <div className="mb-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          Loading payment methods...
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {[1, 2, 3].map((i) => (
                            <span
                              key={i}
                              className="px-3 py-1 text-xs rounded-full bg-muted text-muted-foreground border border-border animate-pulse"
                            ></span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      supportedPaymentMethods.length > 0 && (
                        <div className="mb-4 text-center">
                          <p className="text-sm text-muted-foreground mb-2">
                            Available payment methods
                          </p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {supportedPaymentMethods.map((method) => (
                              <span
                                key={method.id}
                                className={`px-3 py-1 text-xs rounded-full ${
                                  affiliatePaymentMethods.some(
                                    (am) =>
                                      am.paymentMethodId ===
                                      method.paymentMethodId
                                  )
                                    ? "bg-brand-100 text-brand-700 border border-brand-300"
                                    : "bg-muted text-muted-foreground border border-border"
                                }`}
                              >
                                {method.paymentMethod.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                    {!shouldAutoJoin && (
                      <button
                        onClick={handleJoinClick}
                        disabled={isGeneratingCode}
                        className={`w-full flex items-center justify-center text-white py-2 ${appearanceClasses.borderRadius} ${appearanceClasses.shadow} hover:shadow-md transition-all duration-200 transform hover:scale-105`}
                        style={{
                          backgroundColor: colors.primary,
                          filter: "brightness(1)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.filter = "brightness(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.filter = "brightness(1)";
                        }}
                      >
                        {isGeneratingCode ? (
                          <>
                            <Spinner
                              size="sm"
                              className="-ml-1 mr-3 border-white"
                            />
                            Generating code...
                          </>
                        ) : (
                          "Join Now"
                        )}
                      </button>
                    )}
                    {shouldAutoJoin && isGeneratingCode && (
                      <div className="w-full bg-brand-100 text-brand-700 py-2 px-4 rounded-lg text-center">
                        <div className="flex items-center justify-center">
                          <Spinner
                            size="sm"
                            className="-ml-1 mr-3 border-brand-700"
                          />
                          Processing your join request...
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Join Modal */}
        <JoinBrandModal
          isOpen={showJoinModal}
          onClose={() => setShowJoinModal(false)}
          defaultCode={defaultCode}
          username={brandData?.username || ""}
          onSuccess={handleJoinSuccess}
          onAlreadyJoined={handleAlreadyJoined}
          programId={brandData?.program?.id || ""}
          programToken={brandData?.isSharedProgram ? programToken : undefined}
          attributionMethod={brandData?.program?.attributionMethod}
          brandName={brandData?.companyName || undefined}
          shortDiscountUrl={brandData?.shortDiscountUrl}
          adRequestToken={adRequestToken ?? undefined}
        />
      </main>

      {/* Enhanced YouTube Embed Styles */}
      <style>{`
        .youtube-embed-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          height: 0;
          margin: 12px 0 !important;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .youtube-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 8px;
        }
        
        @media (max-width: 640px) {
          .youtube-embed-wrapper {
            padding-bottom: 65%;
            margin: 8px 0 !important;
          }
        }
        
        /* Rich Text Formatting Styles */
        .prose {
          color: inherit;
        }
        
        .prose p {
          margin: 0.5em 0;
          color: inherit;
        }
        
        .prose a {
          color: rgb(var(--brand-600));
          text-decoration: underline;
        }
        
        .prose a:hover {
          color: rgb(var(--brand-700));
        }
        
        /* Text Alignment - Handle both inline styles and TipTap classes */
        .prose p[style*="text-align: center"],
        .prose p[style*="text-align: center;"],
        .prose .text-center,
        .prose [style*="text-align: center"] {
          text-align: center !important;
        }
        
        .prose p[style*="text-align: right"],
        .prose p[style*="text-align: right;"],
        .prose .text-right,
        .prose [style*="text-align: right"] {
          text-align: right !important;
        }
        
        .prose p[style*="text-align: left"],
        .prose p[style*="text-align: left;"],
        .prose .text-left,
        .prose [style*="text-align: left"] {
          text-align: left !important;
        }
        
        /* Force text alignment with higher specificity */
        .prose div[style*="text-align: center"] { text-align: center !important; }
        .prose div[style*="text-align: right"] { text-align: right !important; }
        .prose div[style*="text-align: left"] { text-align: left !important; }
        
        .prose span[style*="text-align: center"] { text-align: center !important; }
        .prose span[style*="text-align: right"] { text-align: right !important; }
        .prose span[style*="text-align: left"] { text-align: left !important; }
        
        /* Bullet Points and Lists - Handle TipTap's HTML structure */
        .prose ul,
        .prose ul[data-type="bulletList"] {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .prose ol,
        .prose ol[data-type="orderedList"] {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .prose li,
        .prose li[data-type="listItem"] {
          margin: 0.25em 0;
          color: inherit;
        }
        
        /* Bold and Italic */
        .prose strong,
        .prose b,
        .prose [data-type="bold"] {
          font-weight: 600;
          color: inherit;
        }
        
        .prose em,
        .prose i,
        .prose [data-type="italic"] {
          font-style: italic;
          color: inherit;
        }
        
        /* Headings */
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          font-weight: 600;
          margin: 1em 0 0.5em 0;
          color: inherit;
        }
        
        .prose h1 { font-size: 1.5em; }
        .prose h2 { font-size: 1.25em; }
        .prose h3 { font-size: 1.125em; }
        
        /* Links */
        .prose a[href] {
          color: rgb(var(--brand-600));
          text-decoration: underline;
          word-break: break-word;
        }
        
        .prose a[href]:hover {
          color: rgb(var(--brand-700));
        }
        
        /* Blockquotes */
        .prose blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #6b7280;
        }
        
        /* Code */
        .prose code {
          background-color: #f3f4f6;
          padding: 0.125em 0.25em;
          border-radius: 0.25em;
          font-family: monospace;
          font-size: 0.875em;
        }
        
        /* Horizontal Rule */
        .prose hr {
          border: none;
          border-top: 1px solid #e5e7eb;
          margin: 1.5em 0;
        }
        
        /* Additional TipTap-specific styles */
        .prose [data-type="paragraph"] {
          margin: 0.5em 0;
        }
        
        .prose [data-type="heading"] {
          font-weight: 600;
          margin: 1em 0 0.5em 0;
        }
        
        /* Handle TipTap's text alignment attributes */
        .prose [style*="text-align: center"],
        .prose [style*="text-align: center;"] {
          text-align: center !important;
        }
        
        .prose [style*="text-align: right"],
        .prose [style*="text-align: right;"] {
          text-align: right !important;
        }
        
        .prose [style*="text-align: left"],
        .prose [style*="text-align: left;"] {
          text-align: left !important;
        }
        
        /* Handle TipTap's data attributes for text alignment */
        .prose [data-text-align="center"] {
          text-align: center !important;
        }
        
        .prose [data-text-align="right"] {
          text-align: right !important;
        }
        
        .prose [data-text-align="left"] {
          text-align: left !important;
        }
        
        /* Override any conflicting styles */
        .prose * {
          text-align: inherit;
        }
        
        .prose [style*="text-align"] {
          text-align: inherit !important;
        }
      `}</style>
    </div>
  );
}
